import { EsaStatus } from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { OUTSCHOOL_TIMEZONE, dayjs } from "@outschool/time";
import {
  EsaSessionType,
  useEsaEndDate,
  useEsaWaitPeriod,
  useSession,
} from "@outschool/ui-auth";
import React from "react";

import * as Activity from "../utils/Activity";

export const classStartsInEsaPurchaseBlackOutPeriod = (
  activity: {
    is_ongoing_weekly: boolean;
  },
  section: {
    allowsLateEnrollment?: boolean;
    nextOngoingMeeting?: { start_time?: string } | null;
    progress?: { nextMeeting?: { start_time?: string } | null } | null;
    start_time?: string | null;
  },
  userTimeZone: string | null,
  esaWaitPeriod: number,
): boolean => {
  let startTime: string | null | undefined = null;
  if (
    dayjs
      .tz(section.start_time, userTimeZone || OUTSCHOOL_TIMEZONE)
      .isBefore() &&
    !activity.is_ongoing_weekly &&
    !section?.allowsLateEnrollment
  ) {
    return true;
  } else if (!!section?.allowsLateEnrollment) {
    startTime = section?.progress?.nextMeeting?.start_time;
  }

  startTime =
    startTime ??
    (section?.nextOngoingMeeting?.start_time || section?.start_time);

  const numDaysUntilStartTime = dayjs
    .tz(dayjs(startTime), userTimeZone || OUTSCHOOL_TIMEZONE)
    .diff(dayjs.tz(userTimeZone || OUTSCHOOL_TIMEZONE), "days");

  return numDaysUntilStartTime < esaWaitPeriod;
};

const _filterDatesByEsaBookingWindow = (
  availableDates: string[],
  userTimeZone: string | null,
  endDateEsaEnrollment: Date | null,
  esaWaitPeriod: number,
): string[] => {
  const esaFundEndDate = dayjs.tz(
    dayjs(endDateEsaEnrollment),
    userTimeZone || OUTSCHOOL_TIMEZONE,
  );
  return availableDates.filter(date => {
    const numDaysUntilStartTime = dayjs
      .tz(dayjs(date), userTimeZone || OUTSCHOOL_TIMEZONE)
      .diff(dayjs.tz(userTimeZone || OUTSCHOOL_TIMEZONE), "days");

    const windowMin = numDaysUntilStartTime >= esaWaitPeriod;

    const windowMax = dayjs
      .tz(dayjs(date), userTimeZone || OUTSCHOOL_TIMEZONE)
      .isSameOrBefore(esaFundEndDate);

    return windowMin && (!endDateEsaEnrollment || windowMax);
  });
};

export const _classRunsBeyondEsaEndDate = (
  activity: {
    is_ongoing_weekly: boolean;
    hasTeacherSchedule: boolean;
  },
  section: {
    end_time?: string | null;
  },
  userTimeZone: string | null,
  enrollment_end_time?: Date | string | null,
  END_DATE_ESA_ENROLLMENT?: Date | null,
): boolean => {
  if (!END_DATE_ESA_ENROLLMENT) {
    return false;
  }

  if (!activity.is_ongoing_weekly && !activity.hasTeacherSchedule) {
    return dayjs
      .tz(dayjs(section.end_time), userTimeZone || OUTSCHOOL_TIMEZONE)
      .isAfter(
        dayjs.tz(
          dayjs(END_DATE_ESA_ENROLLMENT),
          userTimeZone || OUTSCHOOL_TIMEZONE,
        ),
      );
  } else {
    return dayjs
      .tz(dayjs(enrollment_end_time), userTimeZone || OUTSCHOOL_TIMEZONE)
      .isAfter(
        dayjs.tz(
          dayjs(END_DATE_ESA_ENROLLMENT),
          userTimeZone || OUTSCHOOL_TIMEZONE,
        ),
      );
  }
};

export default function useEsaAvailability(
  activity?: {
    esaStatus: EsaStatus;
    is_ongoing_weekly: boolean;
    isClub: boolean;
    hasTeacherSchedule: boolean;
  },
  section?: {
    allowsLateEnrollment?: boolean;
    nextOngoingMeeting?: { start_time?: string } | null;
    progress?: { nextMeeting?: { start_time?: string } | null } | null;
    start_time?: string | null;
    end_time?: string | null;
  },
  enrollment_end_time?: Date | string | null,
): {
  shouldBlockEsaPurchase: boolean;
  blockedEsaPurchaseMessage: string | null;
  filterDatesByEsaBookingWindow: (availableDates: string[]) => string[];
  isMeetingBeyondEsaEnd: (end_time: Date) => {
    isMeetingBeyondEsaEnd: boolean;
    message?: string | null;
  };
} {
  const { userTimeZone, isEsaSession, esaSessionData, esaSessionType } =
    useSession();
  const esaWaitPeriod = useEsaWaitPeriod();
  const ignoreBlackoutPeriod = esaSessionData?.ignoreBlackoutPeriod;
  const { t } = useTranslation(
    "ui-components-website\\hooks\\useEsaAvailability",
  );
  const END_DATE_ESA_ENROLLMENT = useEsaEndDate(!!ignoreBlackoutPeriod);

  const INELIGIBLE_CLASS_MESSAGE = t(
    "This class is not eligible with your ClassWallet funds. Exit the ClassWallet session to pay by other methods.",
  );

  const PAST_ESA_CUTOFF_MESSAGE = t(
    "This class is not available with your ClassWallet funds. The class runs beyond the program deadline of {{ endDate }}.",
    { endDate: dayjs(END_DATE_ESA_ENROLLMENT).format("LL") },
  );

  const filterDatesByEsaBookingWindow = (availableDates: string[]) =>
    _filterDatesByEsaBookingWindow(
      availableDates,
      userTimeZone,
      END_DATE_ESA_ENROLLMENT,
      esaWaitPeriod,
    );

  const isMeetingBeyondEsaEnd = React.useCallback(
    (end_time: Date) => {
      if (!activity) {
        return {
          isMeetingBeyondEsaEnd: false,
        };
      }

      const beyondEsaEnd = _classRunsBeyondEsaEndDate(
        activity,
        {},
        userTimeZone,
        dayjs(end_time).toISOString(),
        END_DATE_ESA_ENROLLMENT,
      );
      return {
        isMeetingBeyondEsaEnd: beyondEsaEnd,
        message: !!beyondEsaEnd ? PAST_ESA_CUTOFF_MESSAGE : null,
      };
    },
    [activity, userTimeZone, END_DATE_ESA_ENROLLMENT, PAST_ESA_CUTOFF_MESSAGE],
  );

  const isClassApprovedForEsaPurchase =
    isEsaSession && !!activity
      ? activity.esaStatus === EsaStatus.Approved
      : true;

  const isFloridaEMASession = esaSessionType === EsaSessionType.EMA;

  if (!activity || !section) {
    let blockedEsaPurchaseMessage: string | null = null;
    if (!isClassApprovedForEsaPurchase) {
      blockedEsaPurchaseMessage = INELIGIBLE_CLASS_MESSAGE;
    }

    return {
      shouldBlockEsaPurchase: !isClassApprovedForEsaPurchase,
      blockedEsaPurchaseMessage,
      filterDatesByEsaBookingWindow,
      isMeetingBeyondEsaEnd,
    };
  }

  // TODO: This additional logic should get moved server side and can
  // be returned as different flags under the `esaStatus` key on the
  // activity query - then can use the sectionStartsInFundingProgramWaitPeriod
  // and sectionRunsBeyondFundingProgramEndDate functions from the
  // funding-programs package
  const isTooLateToEnrollWithEsa = classStartsInEsaPurchaseBlackOutPeriod(
    activity,
    section,
    userTimeZone,
    esaWaitPeriod,
  );

  const isSectionBeyondEsaEnd = _classRunsBeyondEsaEndDate(
    activity,
    section,
    userTimeZone,
    enrollment_end_time,
    END_DATE_ESA_ENROLLMENT,
  );

  const shouldBlockEsaPurchase =
    isEsaSession &&
    (Activity.isClub(activity) ||
      !isClassApprovedForEsaPurchase ||
      isTooLateToEnrollWithEsa ||
      isSectionBeyondEsaEnd);

  let blockedEsaPurchaseMessage;
  if (isFloridaEMASession) {
    blockedEsaPurchaseMessage = t(
      "This class is not eligible with your Florida scholarship funds. Turn off the Florida EMA filter to pay by other methods.",
    );
  } else if (Activity.isClub(activity)) {
    blockedEsaPurchaseMessage = t(
      "Sorry, this class format is not supported with ClassWallet.",
    );
  } else if (!isClassApprovedForEsaPurchase) {
    blockedEsaPurchaseMessage = INELIGIBLE_CLASS_MESSAGE;
  } else if (isTooLateToEnrollWithEsa) {
    blockedEsaPurchaseMessage = t(
      "This class is not available with your ClassWallet funds. Exit the ClassWallet session or select a class that starts in {{numDays}} or more days to allow for ClassWallet approval.",
      { numDays: esaWaitPeriod },
    );
  } else if (isSectionBeyondEsaEnd) {
    blockedEsaPurchaseMessage = PAST_ESA_CUTOFF_MESSAGE;
  } else {
    blockedEsaPurchaseMessage = null;
  }
  return {
    shouldBlockEsaPurchase,
    blockedEsaPurchaseMessage,
    filterDatesByEsaBookingWindow,
    isMeetingBeyondEsaEnd,
  };
}
